import {
  displayInvoiceCompanyName,
  isFullProfitRateCustomer
} from './matrixRules.mjs'

export function allocationsForCustomer(allocations = [], planCustomerId) {
  const targetId = idKey(planCustomerId)
  if (!targetId) return []
  return (allocations || [])
    .filter(item => idKey(item?.fromPlanCustomerId) === targetId)
    .sort(compareDisplayOrder)
}

export function preferencesForCustomer(preferences = [], planCustomerId) {
  const targetId = idKey(planCustomerId)
  if (!targetId) return []
  return (preferences || [])
    .filter(item => idKey(item?.fromPlanCustomerId) === targetId)
    .sort(comparePreferenceOrder)
}

export function downstreamAllocationSummary(allocations = [], customer = null) {
  const rows = allocationsForCustomer(allocations, customer?.id ?? customer?.planCustomerId)
  return summarizeDownstreamRows(rows, customer)
}

export function downstreamDraftSummary(rows = [], customer = null) {
  return summarizeDownstreamRows(rows, customer)
}

export function downstreamTargetInputAvailability({
  target = null,
  savedAllocations = [],
  draftRows = [],
  editingFromPlanCustomerId = null,
  currentRowIndex = -1
} = {}) {
  const targetId = idKey(target?.id ?? target?.planCustomerId)
  const editingFromId = idKey(editingFromPlanCustomerId)
  const targetInputAmount = numberValue(target?.targetInputAmount)
  if (!targetId) {
    return {
      targetInputAmount,
      usedAmount: 0,
      remainingAmount: targetInputAmount
    }
  }

  const savedUsedAmount = (savedAllocations || []).reduce((total, allocation) => {
    if (allocation?.enabled === false) return total
    if (idKey(allocation?.toPlanCustomerId) !== targetId) return total
    if (editingFromId && idKey(allocation?.fromPlanCustomerId) === editingFromId) return total
    return total + numberValue(allocation?.amount)
  }, 0)

  const draftUsedAmount = (draftRows || []).reduce((total, row, index) => {
    if (index === currentRowIndex) return total
    if (row?.enabled === false) return total
    if (idKey(row?.toPlanCustomerId) !== targetId) return total
    return total + numberValue(row?.amount)
  }, 0)

  const usedAmount = savedUsedAmount + draftUsedAmount
  return {
    targetInputAmount,
    usedAmount,
    remainingAmount: Math.max(0, targetInputAmount - usedAmount)
  }
}

function summarizeDownstreamRows(rows = [], customer = null) {
  const enabledRows = rows.filter(item => item?.enabled !== false)
  const totalAmount = enabledRows.reduce((total, item) => total + numberValue(item?.amount), 0)
  const targetAmount = numberValue(customer?.targetOutputAmount)
  const targetNames = enabledRows
    .map(allocationTargetName)
    .filter(Boolean)
  return {
    rows,
    enabledRows,
    enabledCount: enabledRows.length,
    totalAmount,
    targetAmount,
    remainingAmount: Math.max(0, targetAmount - totalAmount),
    overAllocated: targetAmount > 0 && totalAmount > targetAmount,
    targetNames,
    targetNamesText: targetNames.join('、')
  }
}

export function downstreamPreferenceSummary(preferences = [], planCustomerId) {
  const rows = preferencesForCustomer(preferences, planCustomerId)
  const enabledRows = rows.filter(item => item?.enabled !== false)
  const names = enabledRows.map(item => item?.tagName).filter(Boolean)
  return {
    rows,
    enabledRows,
    enabledCount: enabledRows.length,
    shortText: names.length > 0 ? names.slice(0, 3).join('、') : '未设置',
    hiddenCount: Math.max(0, names.length - 3)
  }
}

export function selectableDownstreamCustomers(customers = [], fromPlanCustomerId) {
  const fromId = idKey(fromPlanCustomerId)
  return (customers || [])
    .filter(customer => idKey(customer?.id ?? customer?.planCustomerId) !== fromId)
    .filter(customer => !isFullProfitRateCustomer(customer))
    .map(customer => ({
      ...customer,
      label: displayInvoiceCompanyName(customer)
    }))
}

export function buildChainTree(chain = {}) {
  const nodes = chainNodes(chain)
  const edges = chainEdges(chain)
  if (nodes.length === 0) return []

  const nodeById = new Map()
  const incomingNodeIds = new Set()
  const childrenByNodeId = new Map()

  for (const node of nodes) {
    const key = idKey(node?.id)
    if (key) nodeById.set(key, node)
  }

  for (const edge of edges) {
    const fromKey = idKey(edge?.fromNodeId)
    const toKey = idKey(edge?.toNodeId)
    if (!fromKey || !toKey || !nodeById.has(toKey)) continue
    incomingNodeIds.add(toKey)
    if (!childrenByNodeId.has(fromKey)) {
      childrenByNodeId.set(fromKey, [])
    }
    childrenByNodeId.get(fromKey).push(edge)
  }

  for (const list of childrenByNodeId.values()) {
    list.sort(compareEdgeOrder)
  }

  const rootNodes = nodes.filter(node => {
    const key = idKey(node?.id)
    return key && !incomingNodeIds.has(key)
  })

  const actualRoots = rootNodes.length > 0 ? rootNodes : [nodes[0]]
  return actualRoots.map(node => buildTreeEntry(node, null, childrenByNodeId, nodeById, new Set()))
}

export function chainHasUserSpecifiedEdge(chain = {}) {
  return chainEdges(chain).some(edge => edge?.edgeSource === 'USER_SPECIFIED')
}

export function chainEdges(chain = {}) {
  return chain?.edges || chain?.chainEdges || []
}

export function chainNodes(chain = {}) {
  const nodes = chain?.nodes || chain?.chainNodes || []
  return [...nodes].sort((left, right) => {
    const leftOrder = numberValue(left?.displayOrder ?? left?.nodeOrder ?? left?.sequenceNo)
    const rightOrder = numberValue(right?.displayOrder ?? right?.nodeOrder ?? right?.sequenceNo)
    return leftOrder - rightOrder
  })
}

export function edgeSourceLabel(source) {
  if (source === 'USER_SPECIFIED') return '指定'
  if (source === 'PLATFORM') return '平台'
  return '自动'
}

export function edgeSourceTone(source) {
  if (source === 'USER_SPECIFIED') return 'emerald'
  if (source === 'PLATFORM') return 'indigo'
  return 'slate'
}

function buildTreeEntry(node, incomingEdge, childrenByNodeId, nodeById, path) {
  const nodeKey = idKey(node?.id)
  if (!nodeKey || path.has(nodeKey)) {
    return { node, incomingEdge, children: [], cyclic: true }
  }
  const nextPath = new Set(path)
  nextPath.add(nodeKey)
  const childEdges = childrenByNodeId.get(nodeKey) || []
  return {
    node,
    incomingEdge,
    children: childEdges
      .map(edge => {
        const child = nodeById.get(idKey(edge?.toNodeId))
        if (!child) return null
        return buildTreeEntry(child, edge, childrenByNodeId, nodeById, nextPath)
      })
      .filter(Boolean)
  }
}

function compareDisplayOrder(left, right) {
  const leftOrder = numberValue(left?.displayOrder)
  const rightOrder = numberValue(right?.displayOrder)
  if (leftOrder !== rightOrder) return leftOrder - rightOrder
  return numberValue(left?.id) - numberValue(right?.id)
}

function comparePreferenceOrder(left, right) {
  const leftPriority = numberValue(left?.priority)
  const rightPriority = numberValue(right?.priority)
  if (leftPriority !== rightPriority) return leftPriority - rightPriority
  return compareDisplayOrder(left, right)
}

function compareEdgeOrder(left, right) {
  const leftOrder = numberValue(left?.branchOrder)
  const rightOrder = numberValue(right?.branchOrder)
  if (leftOrder !== rightOrder) return leftOrder - rightOrder
  return numberValue(left?.id) - numberValue(right?.id)
}

function allocationTargetName(allocation) {
  return allocation?.toCompanyName ||
    allocation?.toCompanyNameSnapshot ||
    allocation?.toCustomerName ||
    allocation?.toPlanCustomerName ||
    allocation?.companyNameSnapshot ||
    allocation?.companyName ||
    allocation?.customerName ||
    allocation?.name ||
    ''
}

function idKey(value) {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

function numberValue(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}
